import React, { useState, useEffect, useMemo } from 'react';
import { Typography } from '@mui/material';

import NumberFormat from './util/NumberFormat';
import NumberToKorean from './util/NumberToKorean';

import HS from '../assets/img/HS.png';
import BPS from '../assets/img/bps.png';
import HSLogo from '../assets/img/logo.png';

// 공급자 정보 매핑 객체
const supplierConfig = {
  HS: {
    businessNumberLabel: '등 록 번 호',
    businessNumber: (
      <>
        312-86-46669&nbsp;&nbsp;&nbsp;&nbsp;
        <span style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
          [정보통신공사업_제410148호]
        </span>
      </>
    ),
    companyLabel: (
      <>
        상호(법인명)
      </>
    ),
    company: '(주)에이치에스이노텍',
    representativeLabel: (
      <>
        성&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;명
      </>
    ),
    representative: '박한석',
    addressLabel: '사 업 장 주 소',
    address: '충청남도 천안시 동남구 목천읍 삼성3길 8-61',
    upteLabel: (
      <>
        업&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;태
      </>
    ),
    upte: '건설업, 도.소매',
    jongmokLabel: (
      <>
        업&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;종
      </>
    ),
    materialHeader: [(<>품&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;명</>),(<>규&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;격</>), (<>단 위</>), (<>수&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;량</>), (<>단&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;가</>), (<>공 급 가 액</>), (<>비&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;고</>)],
    jongmok: '정보통신공사업',
    contactPersonLabel: '담당',
    phoneNumberLabel: '전 화 번 호',
    callLabel: '전 화 번 호',
    call: '041-570-9800',
    faxLabel: 'Fax',
    fax: '041-570-9899',
    emailLabel: '이메일',
    headerBgColor: '#9c9c9c',
    materialBorder: '1px solid #000',
    profitLabel: '기업이윤',
    profit: '총합의 5%'
  },

  BPS: {
    businessNumberLabel: '사업자번호',
    businessNumber: (
      <>
        890-81-01420&nbsp;&nbsp;&nbsp;&nbsp;
        <span style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
          정보통신공사업_제410148호
        </span>
      </>
    ),
    construnctionNumberLabel: '공사업번호',
    construnctionNumber: '2025-04-28-001',
    companyLabel: (
      <>
        상&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;호
      </>
    ),
    company: '비피에스테크(주)',
    representativeLabel: '대 표 자',
    representative: '박현정',
    addressLabel: '소 재 지',
    address: '충청남도 당진시 송악읍 중흥1길 51, 세종그랑시아상가 202호',
    upteLabel: (
      <>
        업&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;태
      </>
    ),
    upte: '건설업/도소매',
    jongmokLabel: (
      <>
        종&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;목
      </>
    ),
    materialHeader: ['제품 명','규격', '단위', '수량','단가', '금액', '비고'],
    jongmok: '통신공사업',
    contactPersonLabel: '담 당 자',
    phoneNumberLabel: '연 락 처',
    emailLabel: 'E-mail',
    headerBgColor: '#f0f0f0',
    materialBorder: '1px dotted #999',
    profitLabel: '이윤',
    profit: '5%'
  }
};

// 도장 이미지 경로 매핑 객체
const stampImageConfig = {
  HS: HS,    // (주)에이치에스이노텍 도장 이미지 경로
  BPS: BPS,  // 비피에스테크(주) 도장 이미지 경로
};


// 부가세 별도 여부 매핑 객체
const separateVatConfig = {
  false: '부가세 별도',
  true: '부가세 포함',
};

// 새 작성용 기본 데이터 객체
const emptyData = {
  uploadDate: '',
  estimateNumber: '',
  clientCompany: '',
  contactPerson: '',
  projectName: '',
  email: '',
  phoneNumber: '',
  deliveryDate: '발주 후 2주',
  validityPeriod: '견적일기준',
  separateVat: false,
  items: [],
  totalAmount: 0,
  supplierName: '',
  businessNumber: '',
  representative: '',
  negoAmount: 0,
  includeProfit: false,
  address: '',
  upte: '',
  jongmok: '',
  fax: '',
  etc: '',
  totalAmountWithVat: 0,
};


const EstimateDetailContent = ({ id, formValues = {}, materialRows = [] }) => {
  // 초기 state를 항상 emptyData로 세팅
  const [data, setData] = useState(emptyData);

  // useEffect 분리 초기 세팅
  useEffect(() => {
    // 새 작성 모드: state만 리셋
    if (!id) {
      setData(emptyData);
      return;
    }
  }, [id])

  // 폼 값 또는 자재 리스트 변경시마다 실행
  useEffect(() => {
    // estimate.company 기준으로 매핑 객체에서 정보 꺼내오기
    const supplier = supplierConfig[formValues.company] || {};

    // 특정 ID를 가진 필드의 금액계산
    const computedItems = materialRows.map((row, idx) => {
      // 기본 금액
      const baseAmount = row.materialPrice * row.quantity;
      let extra = 0;

      // ID 14 내 위에 있는 자재 총합의 5%
      if(row.id === 14 || row.materialId === 14){
        const sumAbove = materialRows
            .slice(0, idx)
            .reduce((acc, r) => acc + r.materialPrice * r.quantity, 0);
        extra = Math.floor(sumAbove * 0.05) - 1;
        
      }

      // ID 15 → materialName이 '노무비'인 행들의 합계의 3%
      if (row.id === 15 || row.materialId === 15) {
        // '노무비'인 모든 행을 골라서
        const laborSum = materialRows
              .filter(r => r.materialName === '노무비')
              .reduce((acc, r) => acc + r.materialPrice * r.quantity, 0);
        // 3% 계산
        extra = Math.floor(laborSum * 0.03) - 1;
      }

      // ID 14 & 15 에 대해서만 단가(materialPrice) 재계산
      let newUnitPrice = row.materialPrice;
      if (row.id === 14 || row.id === 15 || row.materialId === 14 || row.materialId === 15) {
        newUnitPrice = baseAmount + extra;
      }
      
      return{
        ...row,
        materialPrice: newUnitPrice,
        amount: baseAmount + extra,
      };
    });

    const total = computedItems.reduce((sum, item) => sum + item.amount, 0);
    // 기업이윤 계산 소수점 버림
    const companyProfit = formValues.includeProfit ? Math.floor(total * 0.05) : 0;
    // 천단위 절삭
    const totalAmount = Math.floor((total + companyProfit) / 10000) * 10000
    
    // 부가세 포함시 부가세 추가 계산 후 변수저장
    let totalAmountWithVat = totalAmount;
    if(formValues.separateVat){
      totalAmountWithVat += Math.floor((totalAmount * 0.1) / 10000) * 10000;
    }
    

    // 이전 데이터를 모두 보존 한 뒤, 변경된 것만 덮어쓰기
    setData(prev => ({
      ...prev,
      items: computedItems,
      totalAmount: totalAmount,
      totalAmountWithVat: totalAmountWithVat,
      companyProfit: companyProfit,
      ...supplier,
      negoAmount: formValues.negoAmount,
    }));
  }, [id, formValues, materialRows]);

  // 폼 값과 기본 데이터 병합
  const merged = useMemo(() => {
    let base = { ...data };

    // formValues override
    if (formValues.company        != null) base.company       = formValues.company;
    if (formValues.clientCompany  != null) base.clientCompany = formValues.clientCompany;
    if (formValues.estimateNumber != null) base.estimateNumber = formValues.estimateNumber;
    if (formValues.uploadDate     != null) base.uploadDate    = formValues.uploadDate ? formValues.uploadDate.split('T')[0].replace(/-/g, '년 ').replace(/(\d{4}년 )(\d{2})년 (\d{2})/, '$1$2월 $3일') : new Date().toISOString().split('T')[0].replace(/-/g, '년 ').replace(/(\d{4}년 )(\d{2})년 (\d{2})/, '$1$2월 $3일');
    if (formValues.contactPerson  != null) base.contactPerson = formValues.contactPerson;
    if (formValues.phoneNumber    != null) base.phoneNumber   = formValues.phoneNumber;
    if (formValues.email          != null) base.email         = formValues.email;
    if (formValues.projectName    != null) base.projectName   = formValues.projectName;
    if (formValues.deliveryDate   != null) base.deliveryDate  = formValues.deliveryDate;
    if (formValues.validityPeriod != null) base.validityPeriod = formValues.validityPeriod;
    if (formValues.separateVat    != null) base.separateVat   = formValues.separateVat;
    if (formValues.includeProfit  != null) base.includeProfit = formValues.includeProfit;
    if (formValues.negoAmount     != null) base.negoAmount    = formValues.negoAmount;
    if (formValues.etc            != null) base.etc           = formValues.etc;

    // 공급사 override
    const comp = formValues.company;
    if (comp && supplierConfig[comp]) {
      base = { ...base, ...supplierConfig[comp] };
    }

    // 부가세 별도 여부 추가
    base.separateVat = separateVatConfig[formValues.separateVat];

    return base;
  }, [data, formValues]);

  // if (!data) return <Typography>데이터를 불러오는 중...</Typography>;

  return (
  <>
    <div style={{ border: '2px solid #000', borderCollapse: 'collapse' }}>
      <div style={{ position: 'relative' }}>
        <div className="image-box" style={{ width: formValues.company === 'HS' ? '70px' : '90px', height: formValues.company === 'HS' ? '70px' : '90px', position: 'absolute', top: formValues.company === 'HS' ? '90px' : '70px', right: formValues.company === 'HS' ? '0px' : '-20px', pointerEvents: 'none', zIndex: 999 }}>
          <img src={stampImageConfig[formValues.company]} alt="도장" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      </div>
      {/* 상단 헤더 영역 */}

      {/* 에이치에스이노텍 */}
      {merged.company === '(주)에이치에스이노텍' && (
        <>
        <span style={{ fontSize: '14px', borderBottom: '1px solid #000'}}>
          NO. {merged.estimateNumber ? merged.estimateNumber.split('-')[1] : ''}
        </span>
        <div style={{ position: 'relative'}}>
          <div className="HS-box" style={{ width: '90px', height: '90px', position: 'absolute', top: '-25px', right: '10px', pointerEvents: 'none', zIndex: 999 }}>
            <img src={HSLogo} alt="로고" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
        </div>
        </>
      )}

      {/* 중앙 큰글씨로 견 적 서 */}
      <Typography variant="h4" style={{ textAlign: 'center', marginTop: '10px', marginBottom: merged.company === '비피에스테크(주)' ? '20px' : '0px'}}>견&nbsp;&nbsp;&nbsp;&nbsp;적&nbsp;&nbsp;&nbsp;서</Typography>
      {merged.company === '(주)에이치에스이노텍' && (
        <Typography style={{ fontSize: '13px' }}>견적일자 : {merged.uploadDate}</Typography>
      )}
      <table style={{ width: '100%',border: merged.company === '비피에스테크(주)' ? '1px solid #000' : '0px', borderRight: '0px', borderLeft: '0px', borderCollapse: 'collapse', fontSize: '14px'}}>
        <tbody style={{ textAlign: 'center' }}>
          <tr>
            {merged.company === '(주)에이치에스이노텍' ? (
              <td style={{width: '37%', fontSize: '14px', fontWeight: 'bold', textAlign: 'left'}}>견적명 : {merged.projectName}</td>
            ) : (
              <td rowSpan={2} style={{width: '35%', fontSize: '18px', fontWeight: 'bold'}}><span style={{borderBottom: '1px solid #000'}}>{merged.clientCompany ? `${merged.clientCompany} 귀중` : ''}</span></td>
            )}
            <td rowSpan={6} style={{ backgroundColor: merged.headerBgColor, width: 35, border: '1px solid #000', borderTop: merged.company === '비피에스테크(주)' ? '1px solid #000' : '2px solid #000'}}>공<br/><br/>급<br/><br/>자</td>
            <td style={{backgroundColor: merged.headerBgColor, border: '1px solid #000', borderLeft: '1px solid #000', borderTop: merged.company === '비피에스테크(주)' ? '1px solid #000' : '2px solid #000'}}>{merged.businessNumberLabel}</td>
            <td colSpan={3} style={{ border: '1px solid #000', borderRight: '0px', textAlign: 'left', paddingLeft: '10px', borderTop: merged.company === '비피에스테크(주)' ? '1px solid #000' : '2px solid #000'}}>{merged.businessNumber}</td>
          </tr>
          <tr>
            {merged.company === '(주)에이치에스이노텍' && (
              <>
                <td></td>
              </>
            )}
            <td style={{ backgroundColor: merged.headerBgColor, border: '1px solid #000', width: '15%', padding: 3 }}>{merged.companyLabel}</td>
            <td style={{ border: '1px solid #000', fontSize: formValues.company === 'HS' ? '13px' : '14px' }}>{merged.company}</td>
            <td style={{ border: '1px solid #000', backgroundColor: merged.headerBgColor, width: '10%'}}>{merged.representativeLabel}</td>
            <td style={{ border: '1px solid #000', borderRight: '0px', width: '15%' }}>{merged.representative}</td>
          </tr>
          <tr>
            {merged.company === '(주)에이치에스이노텍' ? (
              <td rowSpan={2} style={{ fontSize: '14px' }}>
                <div style={{ borderBottom: '2px solid #000', width: '80%', marginLeft: '5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center', flex: 1, }}>{merged.clientCompany}</span>
                  <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '10px', whiteSpace: 'nowrap' }}>귀중</span>
                </div>
              </td>
            ) : (
              <td rowSpan={2} style={{ fontSize: '18px' }}>아래와 같이 견적합니다</td>
            )}
            <td style={{ backgroundColor: merged.headerBgColor, border: '1px solid #000' }}>{merged.addressLabel}</td>
            <td colSpan={3} style={{ textAlign: 'left', paddingLeft: 10 }}>
              {merged.address}
            </td>
          </tr>
          <tr>
            <td style={{ backgroundColor: merged.headerBgColor, border: '1px solid #000', padding: 3 }}>{merged.upteLabel}</td>
            <td style={{ border: '1px solid #000' }}>{merged.upte}</td>
            <td style={{ border: '1px solid #000', backgroundColor: merged.headerBgColor }}>{merged.jongmokLabel}</td>
            <td style={{ border: '1px solid #000', borderRight: 0 }}>{merged.jongmok}</td>
          </tr>
          <tr>
            {merged.company === '(주)에이치에스이노텍' ? (
              <td rowSpan={2} style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'left' }}>담당 : {merged.contactPerson} {merged.phoneNumber}</td>
            ) : (
              <td rowSpan={2}>{merged.uploadDate}</td>
            )}
            <td style={{ backgroundColor: merged.headerBgColor, border: '1px solid #000', padding: 3 }}>{merged.company === '(주)에이치에스이노텍' ? merged.callLabel : merged.contactPersonLabel}</td>
            <td style={{ border: '1px solid #000' }}>{merged.company === '(주)에이치에스이노텍' ? merged.call : merged.contactPerson}</td>
            <td style={{ border: '1px solid #000', backgroundColor: merged.headerBgColor }}>{merged.company === '(주)에이치에스이노텍' ? merged.faxLabel : merged.phoneNumberLabel}</td>
            <td style={{ border: '1px solid #000', borderRight: 0 }}>{merged.company === '(주)에이치에스이노텍' ? merged.fax : merged.phoneNumber}</td>
          </tr>
        </tbody>
      </table>

      {/* 비피에스테크(주) */}
      {merged.company === '비피에스테크(주)' ? (
      <>
        <div style={{textAlign: 'right', fontSize: '12px'}}>{merged.emailLabel} : {merged.email}</div>
        {/* 고객 정보 영역 */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '20px'}}>
          <tbody>
            <tr style = {{}}>
              <td style={{ borderTop: '2px solid #000', borderBottom: '1px dotted #999', padding: 3, backgroundColor: '#fafafa', width: '15%', textAlign: 'center' }}>견적번호</td>
              <td style={{ borderTop: '2px solid #000', borderBottom: '1px dotted #999', borderLeft: '1px dotted #999', padding: 3, width: '40%'}}>{merged.estimateNumber}</td>
              <td style={{ borderTop: '2px solid #000', borderBottom: '1px dotted #999', borderLeft: '1px dotted #999', padding: 3, width: '15%' , backgroundColor: '#fafafa', textAlign: 'center'}}>납 기 일</td>
              <td style={{ borderTop: '2px solid #000', borderBottom: '1px dotted #999', borderLeft: '1px dotted #999', padding: 3, width: '30%'}}>{merged.deliveryDate}</td>
            </tr>
            <tr>
              <td style={{ borderBottom: '1px dotted #999', padding: 3, backgroundColor: '#fafafa', textAlign: 'center' }}>공 사 명</td>
              <td colSpan={3} style={{ borderBottom: '1px dotted #999', borderLeft: '1px dotted #999', padding: 3 }}>{merged.projectName}</td>
            </tr>
            <tr>
              <td style={{ borderBottom: '1px dotted #999', padding: 3, backgroundColor: '#fafafa', textAlign: 'center' }}>결제방법</td>
              <td style={{ borderBottom: '1px dotted #999', borderLeft: '1px dotted #999', padding: 3 }}>협의</td>
              <td style={{ borderBottom: '1px dotted #999', borderLeft: '1px dotted #999', padding: 3, width: '15%' , backgroundColor: '#fafafa', textAlign: 'center'}}>견적유효기간</td>
              <td style={{ borderBottom: '1px dotted #999', borderLeft: '1px dotted #999', padding: 3 }}>{merged.validityPeriod}</td>
            </tr>
            <tr>
              <td style={{ padding: 3, backgroundColor: '#fafafa' , borderBottom: '2px solid #000', textAlign: 'center'}}>금&nbsp;&nbsp;&nbsp;&nbsp;액</td>
              <td colSpan={3} style={{ padding: 3,  borderBottom: '2px solid #000', borderLeft: '1px dotted #999', paddingLeft: '30px'}}>일금 <span style={{fontWeight: 'bold'}}><NumberToKorean num={formValues.negoAmount > 0 && formValues.negoAmount !== null && formValues.negoAmount !== '' && formValues.negoAmount !== undefined ? Math.floor(formValues.negoAmount) : Math.floor(merged.totalAmountWithVat)}/></span>정 &nbsp;&nbsp;&nbsp;&nbsp; (&#8361; <NumberFormat value={formValues.negoAmount > 0 && formValues.negoAmount !== null && formValues.negoAmount !== '' && formValues.negoAmount !== undefined ? Math.floor(formValues.negoAmount) : Math.floor(merged.totalAmountWithVat)}/>) {merged.separateVat}</td>
            </tr>
          </tbody>
        </table>
      </>
      ) : (
        <>
          <div style={{fontSize: '14px', fontWeight: 'bold'}}>{merged.emailLabel} : {merged.email}</div>
        </>
      )}

      {merged.company === '(주)에이치에스이노텍' && (
        <>
        <table style={{width: '100%', borderTop: '2px solid #000', borderBottom: '1px solid #000', borderCollapse: 'collapse'}}>
          <tbody>
            <tr>
            <td style={{backgroundColor: merged.headerBgColor, textAlign: 'center', padding: 3, fontSize: '14px', fontWeight: 'bold', width: '20%'}}>합 계 금 액</td>
            <td colSpan={6} style={{ textAlign: 'center'}}>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '90%', alignItems: 'center', margin: '0 auto' }}>
                <div style={{fontSize: '17px', fontWeight: 'bold'}}>
                  견적 금액 : 一金 <NumberToKorean num={merged.negoAmount > 0 && merged.negoAmount !== null && merged.negoAmount !== '' && merged.negoAmount !== undefined ? Math.floor(merged.negoAmount) : Math.floor(merged.totalAmountWithVat)}/> 정
                </div>
                <div style={{fontSize: '17px', fontWeight: 'bold'}}>
                  (&#8361; <NumberFormat value={merged.negoAmount > 0 ? Math.floor(merged.negoAmount) : Math.floor(merged.totalAmountWithVat)}/> {merged.separateVat})
                </div>
              </div>
              </td>
            </tr>
          </tbody>
        </table>
        </>
      )}
      {/* 품목 리스트 영역 */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
        <thead>
          <tr>
            {merged.materialHeader.map((h, i) => (
              <th key={i} style={{ padding: 3, textAlign: 'center', backgroundColor: merged.headerBgColor, border: merged.materialBorder, borderTop: '1px solid #000', borderLeft: i === 0 ? '0px' : '', borderRight: i === 6 ? '0px' : merged.materialBorder}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {merged.items.map((row, idx) => (
            <tr key={idx}>
              <td style={{ padding: 3, border: merged.materialBorder, width: '28%', borderLeft: '0px'}}>{row.materialName}</td>
              <td style={{ padding: 3, textAlign: merged.company === '비피에스테크(주)' ? 'left' : 'center', border: merged.materialBorder, width: '28%'}}>{row.materialSpec}</td>
              <td style={{ padding: 3, textAlign: 'center', border: merged.materialBorder, width: '5%' }}>{row.materialUnit}</td>
              <td style={{ padding: 3, textAlign: 'right', border: merged.materialBorder, width: '9%' }}>{row.quantity}</td>
              <td style={{ padding: 3, textAlign: 'right', border: merged.materialBorder, width: '10%' }}><NumberFormat value={row.materialPrice} /></td>
              <td style={{ padding: 3, textAlign: 'right', border: merged.materialBorder, width: '10%'}}><NumberFormat value={row.amount} /></td>
              <td style={{ padding: 3, textAlign: 'center', border: merged.materialBorder, width: '20%', fontSize: (idx === 6)? '10px' : '', borderRight: '0px'}}>{row.note}</td>
            </tr>
          ))}
          {formValues.includeProfit && (
            <tr>
              <td style={{padding: 3, border: merged.materialBorder, width: '28%', borderLeft: '0px' }}>{merged.profitLabel}</td>
              <td style={{padding: 3, border: merged.materialBorder, width: '28%', textAlign: merged.company === '비피에스테크(주)' ? 'left' : 'center'}}>{merged.profit}</td>
              <td style={{padding: 3, border: merged.materialBorder, width: '5%', textAlign: 'center'}}>식</td>
              <td style={{padding: 3, border: merged.materialBorder, width: '9%', textAlign: 'right'}}>1</td>
              <td style={{padding: 3, border: merged.materialBorder, width: '10%', textAlign: 'right'}}><NumberFormat value={merged.companyProfit}/></td>
              <td style={{padding: 3, border: merged.materialBorder, width: '10%', textAlign: 'right'}}><NumberFormat value={merged.companyProfit}/></td>
              <td style={{padding: 3, border: merged.materialBorder, width: '20%', textAlign: 'right', fontSize: '10px', borderRight: '0px'}}></td>
            </tr>
          )}
          {merged.items.length < 15 && formValues.company === 'HS' &&
            Array.from({ length: 15 - merged.items.length }).map((_, i) => (
              <tr key={`empty-row-${i}`}>
                <td style={{padding: 3, border: merged.materialBorder, width: '28%', borderLeft: '0px'}}><br/></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '28%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '5%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '9%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '10%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '10%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '20%', borderRight: '0px'}}></td>
              </tr>
            ))
          }
          {merged.company === '비피에스테크(주)' && (
            <>
              <tr>
                <td style={{padding: 3, border: merged.materialBorder, width: '28%', textAlign: 'center', borderLeft: '0px'}}>합 계 금 액</td>
                <td style={{padding: 3, border: merged.materialBorder, width: '28%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '5%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '9%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '10%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '10%', textAlign: 'right'}}><NumberFormat value={Math.floor(merged.totalAmount)}/></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '20%', fontSize: '10px', borderRight: '0px', textAlign: 'right'}}>천단위 절삭</td>
              </tr>
              {formValues.separateVat && (
                <tr>
                  <td style={{padding: 3, border: merged.materialBorder, width: '28%', textAlign: 'center', borderLeft: '0px'}}>부 가 세</td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '28%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '5%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '9%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '10%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '10%', textAlign: 'right'}}><NumberFormat value={Math.floor(merged.totalAmount / 10)}/></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '20%', textAlign: 'right', fontSize: '10px', borderRight: '0px'}}></td>
                </tr>
              )}
              <tr>
                <td style={{padding: 3, border: merged.materialBorder, width: '28%', textAlign: 'center', fontWeight: 'bold', borderLeft: '0px'}}>견 적 가</td>
                <td style={{padding: 3, border: merged.materialBorder, width: '28%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '5%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '9%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '10%'}}></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '10%', textAlign: 'right'}}><NumberFormat value={Math.floor(merged.totalAmountWithVat)}/></td>
                <td style={{padding: 3, border: merged.materialBorder, width: '20%', textAlign: 'right', fontSize: '10px', borderRight: '0px'}}></td>
              </tr>
              {formValues.negoAmount > 0 && (
                <tr>
                  <td style={{padding: 3, border: merged.materialBorder, width: '28%', textAlign: 'center', fontWeight: 'bold', borderLeft: '0px'}}>최종 견적가</td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '28%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '5%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '9%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '10%'}}></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '10%', textAlign: 'right'}}><NumberFormat value={Math.floor(merged.negoAmount)}/></td>
                  <td style={{padding: 3, border: merged.materialBorder, width: '20%', textAlign: 'right', fontSize: '10px', borderRight: '0px'}}></td>
                </tr>
              )}
            </>
          )}
        </tbody>
        <tfoot>
          {merged.company === '비피에스테크(주)' ? (
            <>
            <tr style={{ backgroundColor: merged.headerBgColor }}>
              <td colSpan='7' style={{ fontWeight: 'bold', textAlign: 'center'}}> 주 품목 : 네트워크 구축및 관리유지보수, 구내통신공사, CCTV, 방송장비등 </td>
            </tr>
            </>
          ) : (
            <>
              <tr style={{ borderTop: '2px solid #000' }}>
                <td colSpan='5' style={{ textAlign: 'center', border: '1px solid #000', borderLeft: '0px' }}>합 계</td>
                <td style={{ textAlign: 'right', border: '1px solid #000' }}><NumberFormat value={merged.totalAmount}/></td>
                <td style={{ border: '1px solid #000', borderRight: '0px', textAlign: 'right', fontSize: '11px' }}>천단위 절삭</td>
              </tr>
              {formValues.separateVat &&(
                <tr>
                  <td colSpan='5' style={{ textAlign: 'center', fontWeight: 'bold', border: '1px solid #000', borderLeft: '0px' }}>V A T</td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold', border: '1px solid #000' }}><NumberFormat value={Math.floor(merged.totalAmount / 10)}/></td>
                  <td style={{ border: '1px solid #000', borderRight: '0px' }}></td>
                </tr>
              )}
              <tr style={{ backgroundColor: merged.headerBgColor, borderBottom: merged.negoAmount > 0 ? '0px' : '2px solid #000' }}>
                <td colSpan='5' style={{ textAlign: 'center', fontWeight: 'bold', border: '1px solid #000', borderLeft: '0px' }}>견 적 가</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold', border: '1px solid #000' }}><NumberFormat value={Math.floor(merged.totalAmountWithVat)}/></td>
                <td style={{ border: '1px solid #000', borderRight: '0px', textAlign: 'center', fontSize: '11px' }}></td>
              </tr>
              {merged.negoAmount > 0 && (
                <tr style={{ borderBottom: '2px solid #000', backgroundColor: merged.headerBgColor, fontWeight: 'bold'}}>
                  <td colSpan='5' style={{ textAlign: 'center', border: '1px solid #000', borderLeft: '0px' }}>최종 견적가 (Nego)</td>
                  <td style={{ textAlign: 'right', border: '1px solid #000' }}><NumberFormat value={Math.floor(merged.negoAmount)} /></td>
                  <td style={{ border: '1px solid #000', borderRight: '0px' }}></td>
                </tr>
              )}
              <tr>
                <td colSpan='7' style={{  }}>※기타 사항 1. 견적유효 기간 : {merged.validityPeriod}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 2. 납품 기간 : {merged.deliveryDate}</td>
              </tr>
              <tr>
                <td colSpan='7' style={{  }}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;3. 결재 조건 : 협의&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 4. 무상 하자 : 준공완료 후 1년</td>
              </tr>
              <tr>
                <td colSpan='7' style={{ whiteSpace: 'pre-wrap' }}>{merged.etc}</td>
              </tr>
              <tr>
                <td colSpan='7' style={{ fontSize: '14px', fontWeight: 'bold', backgroundColor: merged.headerBgColor }}>주요품목 : 서버관리 네트워크 관리, 구내통신공사, CCTV, PBX, 구내전화, 방송장비등</td>
              </tr>
            </>
          )}
        </tfoot>
      </table>
    </div>
    {merged.company === '(주)에이치에스이노텍' ? (
      <Typography style={{fontSize: '14px', fontWeight: 'bold', textAlign: 'right'}}>http://www.hskt.kr</Typography>
    ) : (
      <span style={{ whiteSpace: 'pre-wrap', fontSize: '12px', paddingLeft: '10px' }}>{merged.etc}</span>
    )}
  </>
  );
};

export default EstimateDetailContent;